import { useEffect, useState } from 'react'

import { LazyImage } from '@/components/LazyImage'
import { cn } from '@/lib/utils'

const ADOBE_CLIENT_ID = '3daf1a0430a045879e5bf8163cad53c3'

export type PdfOrImageSource = { url: string } | { file: File } | null

function isPdfByUrl(url: string): boolean {
  return url.toLowerCase().endsWith('.pdf')
}

function isPdfByFile(file: File): boolean {
  return file.type === 'application/pdf'
}

interface PdfOrImageViewerProps {
  /** Either a URL (string) or a File. Null hides the viewer. */
  source: PdfOrImageSource
  /** Unique ID for the div. */
  id: string
  /** Optional class for the container (e.g. height). */
  className?: string
  /** Alt text for images. */
  alt?: string
  /** File name shown in Adobe viewer metadata (for PDF). */
  fileName?: string
  /** LazyImage aspect ratio when showing an image from URL. */
  imageAspectRatio?: 'square' | '4/3' | '16/9' | 'auto'
  /**
   * Optional cache-bust value (e.g. timestamp or version).
   * Appended as ?v= or &v= so the browser fetches fresh content when the same URL is overwritten (e.g. replaced receipt).
   */
  cacheBust?: string | number
}

declare global {
  interface Window {
    AdobeDC?: {
      View: new (config: { clientId: string; divId: string }) => {
        previewFile: (
          config: {
            content: { location?: { url: string }; promise?: Promise<ArrayBuffer> }
            metaData: { fileName: string }
          },
          viewerConfig: { embedMode: string }
        ) => void
      }
    }
  }
}

/**
 * Renders a PDF (via Adobe DC View) or an image (img / LazyImage).
 * Use for displaying a document from a URL or a File (e.g. receipt, barcode).
 */
function urlWithCacheBust(url: string, cacheBust?: string | number): string {
  if (!url) return url
  const param = cacheBust !== undefined ? String(cacheBust) : encodeURIComponent(url)
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}v=${param}`
}

export function PdfOrImageViewer({
  source,
  id,
  className,
  alt = 'Document preview',
  fileName = 'document.pdf',
  imageAspectRatio = 'auto',
  cacheBust,
}: PdfOrImageViewerProps) {
  const divId = `adobe-pdf-view-${id}`

  const [fileObjectUrl, setFileObjectUrl] = useState<string | null>(null)

  const isUrl = source !== null && 'url' in source
  const isFile = source !== null && 'file' in source
  const url = isUrl ? source.url : null
  const displayUrl = url ? urlWithCacheBust(url, cacheBust) : null
  const file = isFile ? source.file : null

  const isPdf = isUrl && url ? isPdfByUrl(url) : isFile && file ? isPdfByFile(file) : false
  const isImage = source !== null && !isPdf

  // Cleanup blob URL when source is a file and we created an object URL
  useEffect(() => {
    return () => {
      if (fileObjectUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileObjectUrl)
        setFileObjectUrl(null)
      }
    }
  }, [fileObjectUrl])

  // Create object URL for image File so we can pass a string to img
  useEffect(() => {
    if (!isImage || !file) return
    const objectUrl = URL.createObjectURL(file)
    setFileObjectUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
      setFileObjectUrl(null)
    }
  }, [isImage, file])

  // Render Adobe PDF viewer for URL
  useEffect(() => {
    if (!isPdf || !displayUrl || !isUrl) return

    const timer = setTimeout(() => {
      const adobeDiv = document.getElementById(divId)
      if (!adobeDiv || typeof window.AdobeDC?.View !== 'function') return

      adobeDiv.innerHTML = ''
      const adobeDCView = new window.AdobeDC.View({
        clientId: ADOBE_CLIENT_ID,
        divId,
      })
      adobeDCView.previewFile(
        {
          content: { location: { url: displayUrl } },
          metaData: { fileName },
        },
        { embedMode: 'IN_LINE' }
      )
    }, 100)

    return () => clearTimeout(timer)
  }, [isPdf, isUrl, displayUrl, divId, fileName])

  // Render Adobe PDF viewer for File (PDF)
  useEffect(() => {
    if (!isPdf || !file || !isFile) return

    const timer = setTimeout(() => {
      const adobeDiv = document.getElementById(divId)
      if (!adobeDiv || typeof window.AdobeDC?.View !== 'function') return

      adobeDiv.innerHTML = ''
      const adobeDCView = new window.AdobeDC.View({
        clientId: ADOBE_CLIENT_ID,
        divId,
      })

      const reader = new FileReader()
      reader.onloadend = e => {
        const filePromise = Promise.resolve(e.target?.result)
        adobeDCView.previewFile(
          {
            content: { promise: filePromise as Promise<ArrayBuffer> },
            metaData: { fileName: file.name },
          },
          { embedMode: 'IN_LINE' }
        )
      }
      reader.readAsArrayBuffer(file)
    }, 100)

    return () => clearTimeout(timer)
  }, [isPdf, isFile, file, divId])

  if (!source) return null

  if (isPdf) {
    return (
      <div className={cn('w-full rounded', className)}>
        <div id={divId} className='h-96 w-full rounded' />
      </div>
    )
  }

  // Image: from URL use LazyImage, from File use object URL
  if (isUrl && displayUrl) {
    return (
      <div className={cn('w-full', className)}>
        <LazyImage src={displayUrl} alt={alt} aspectRatio={imageAspectRatio} containerClassName='rounded-lg' className='max-h-64 w-auto' />
      </div>
    )
  }

  if (isFile && fileObjectUrl) {
    return (
      <div className={cn('flex justify-center', className)}>
        <img src={fileObjectUrl} alt={alt} className='max-h-64 w-auto rounded border' />
      </div>
    )
  }

  return null
}
